import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Field, Formik, FormikProps } from 'formik';
import { Box } from '@mui/material';
import clsx from 'clsx';
import { useHistory } from '@useHistory/useHistoryCompat';
import {
  AUTH_ACC_CREATED_AT_QUERY_FLAG,
  AUTH_ACC_CREATED_QUERY_FLAG
} from '../../../connector-form/constants/constants';
import { DESTINATION_TYPES } from '../../../../containers/destination/constants/destinationTypes';
import { isPromiseCancelError } from '../../../../core/utils/promise';
import { TargetFactory } from '../../../../containers/activate-target/utils';
import {
  getBackendErrorCodeFromObj,
  getErrorMessageFromObj
} from '../../../../core/utils/request';
import { AuthorisedAccount } from '../../../connector-form/interfaces/auth';
import { ReactRouterService } from '../../../../core/services/react-router.service';
import { getAuthEnvironmentLabel } from '../auth-environment-selection-dialog/utils';
import { AuthAccountEnvironment } from '../auth-environment-selection-dialog/interface';
import { ConnectorAuthTypeEnum } from '../../../connector-form/interfaces/authType';
import {
  HdButton,
  HdDocLink,
  HdTooltip,
  HdPane,
  HdFormControl
} from '../../../../components';
import { WizardStepBaseProps } from '../../../../wizard/interfaces/wizardStep';
import { useWizardStepData } from '../../../../wizard/hooks/useWizardStepData';
import AuthAPI from '../../../../auth/api/AuthAPI';
import { AuthAccountsBox } from '../auth-account-box/AuthAccountBox';
import { NewAuthTrigger } from '../new-auth-trigger/NewAuthTrigger';
import styles from './AuthSelect.module.scss';
import {
  authorizedAccountsFactory,
  fetchErrorMessageAndRedirect,
  getAddAccountSuccessRedirectionPath,
  removeParamAndFetchUrl
} from '../../../connector-form/utils/authorisedAccount';
import {
  ADS_ACCOUNT_SETUP_DOC_LINK_MAP,
  AUTH_ERROR_MESSAGE_PARAM_NAME,
  AUTH_FAIL_DOC_LINK_MAP,
  SOURCE_INFO_MESSAGE_PANE_MAP
} from '../../constants';
import { SourceTermsAndServices } from '../SourceTermsAndServices';
import {
  TRACKER_ADD_AUTH_ACCOUNT_CLICK,
  TRACKER_ADD_FIRST_AUTH_ACCOUNT_CLICK,
  TRACKER_ADD_MORE_AUTH_ACCOUNT_CLICK,
  TRACKER_AUTH_ACCOUNT_CLICK,
  TRACKER_AUTH_ACCOUNT_CONTINUE,
  TRACKER_AUTH_ACCOUNT_CONTINUE_CLICK,
  TRACKER_VALID_ADS_ACCOUNT_FAILED,
  TRACKER_NEW_TARGET_FAILED,
  TRACKER_NEW_TARGET_SUBMIT_CLICK,
  TRACKER_NEW_TARGET_SUCCESS,
  TRACKER_SOURCE_CONFIG_INVITE_MEMBER_LINK_CLICK,
  TRACKER_NEW_AUTH_ACCOUNT
} from '../../../connector-form/tracking/connectorFormTracking';
import { DestinationMode } from '../../../../core/interfaces/destination-mode';
import { getDestinationModeKeywords } from '../../../connector-form/utils/utils';
import { SOURCE_TYPES } from '../../../../containers/pipeline/interfaces/source-type';
import { TargetTypesEnum, TARGET_TYPES } from '../../../../containers/activate-target/models/target-type';
import RetryApiAlert from '../../../../fallback-screens/components/retry-api-alert/RetryApiAlert';
import {
  AccountEntityTypes,
  AuthAccountProvider
} from '../../interfaces';
import useAnalyticsTracker from '../../../../analytics/hooks/useAnalyticsTracker';
import { AuthEnvironmentSelection } from '../auth-environment-selection-dialog';
import { AdsAccountError } from '../ads-account-error/AdsAccountError';
import { TARGET_NAME, validateAdsAccountRequest, validationSchema } from '../../utils/utils';
import TargetAPI from '../../../../containers/activate-target/ActivateTargetAPI';
import ErrorFocus from '../../../../form/components/error-focus/ErrorFocus';
import HdFormikTextFieldValidator from '../../../../form/formik/components/text-field-with-async-validation/HdFormikTextFieldValidator';
import useQueue from '../../../../core/hooks/useQueue';
import FormError from '../../../../form/components/form-error/FormError';
import { ShareDocDialogService } from '../../../../containers/team/InviteMembersDialog/share-doc-dialog.service';
import { HevoEntity } from '../../../../core/interfaces/hevo-entity';
import { getNextEntityNameForDefaultName } from '../../../../core/utils/entity';
import useService from '../../../../injector/hooks/useService';
import { getDataIdGenerator } from '../../../../core/utils/generateDataId';
import { HdWizardStepController } from '../../../../wizard/wizard-native/hd-wizard-step-controller';

interface IAuthSelectProps {
  data: any;
  stepControllerData: {
    provider: AuthAccountProvider;
    targetType?: TargetTypesEnum;
    destinationMode?: DestinationMode;
  };
  params: any;
  markDirty: Function;
  updateData: (data) => void;
  continueNext: (formData: any) => void;
}

export const generateDataId = getDataIdGenerator('select-auth');

function LoadingShimmer() {
  return (
    <div data-id={generateDataId('shimmer')}>
      <div
        className='center-flex-row cursor-hand px-5 justify-between border-bottom'
        style={{ height: '64px' }}
      >
        <div className='shimmer shimmer-line' style={{ height: '12px', width: '120px' }} />
        <div className='shimmer shimmer-line' style={{ height: '12px', width: '50px' }} />
      </div>

      <div className='center-flex-row cursor-hand px-5 justify-between' style={{ height: '64px' }}>
        <div className='shimmer shimmer-line' style={{ height: '12px', width: '150px' }} />
        <div className='shimmer shimmer-line' style={{ height: '12px', width: '50px' }} />
      </div>
    </div>
  );
}

interface AuthSelectProps extends Omit<WizardStepBaseProps, 'controller'> {
  controller: Pick<HdWizardStepController, 'params' | 'data'>;
}

export function AuthSelect({
  initialData,
  controller,
  markDirty,
  onDataChange,
  completeStep
}: AuthSelectProps) {
  const [data, setData] = useWizardStepData(initialData, onDataChange);

  const updateData = d => {
    setData(s => ({
      ...s,
      ...d
    }));
  };

  const continueNext = d => {
    updateData(d);
    completeStep();
  };

  return (
    <AuthSelectInner
      continueNext={continueNext}
      params={controller.params}
      data={data}
      markDirty={markDirty}
      updateData={updateData}
      stepControllerData={controller.data}
    />
  );
}

export function AuthSelectInner({
  continueNext,
  data,
  markDirty,
  updateData,
  params,
  stepControllerData
}: IAuthSelectProps) {
  const {
    provider,
    destinationMode = DestinationMode.DESTINATION,
    targetType
  } = stepControllerData;
  const authenticationEntityType = params.entityType;

  const angularRouterService = useService(ReactRouterService);
  const history = useHistory();

  const [consentUrls, setConsentUrls] = useState<{ url: string; env: AuthAccountEnvironment }[]>(
    []
  );
  const [authorisedAccounts, setAuthorisedAccounts] = useState<AuthorisedAccount[]>([]);

  const [loadingAuthAccounts, setLoadingAuthAccounts] = useState(false);
  const [loadingTargetNames, setLoadingTargetNames] = useState(false);
  const [defaultTargetName, setDefaultTargetName] = useState('');
  const [error, setPageError] = useState();
  const [adsAccountError, setAdsAccountError] = useState(false);
  const [targetFormError, setTargetFormError] = useState(null);
  const [redirectedErrorMessage, setRedirectedErrorMessage] = useState<string>(null);
  const [submitting, setSubmitting] = useState(false);
  const [targetValidationFlow, setTargetValidationFlow] = useState(false);
  const [authorisedAccountId, setAuthorisedAccountId] = useState<number>(data.account?.id);
  // const isFBAShowInviteTeamMemberCTAExpVariant = fbaOAuthExperimentService.isExperimentVariant();
  const [showEnvironmentSelection, setShowEnvironmentSelection] = useState(false);

  const [_, addProcess, removeProcess, queueLength] = useQueue();
  const formikRef = useRef<
    FormikProps<{
      targetName: string;
    }>
  >();

  const targetName = formikRef.current?.values?.targetName;

  const { eventTrack } = useAnalyticsTracker();

  const authorisedAccountEmail = useMemo(
    () => authorisedAccounts.find(acc => acc.id === authorisedAccountId)?.user,
    [authorisedAccountId, authorisedAccounts]
  );

  useEffect(() => {
    setRedirectedErrorMessage(fetchErrorMessageAndRedirect(angularRouterService));
  }, []);

  const shareDialogService = useService<ShareDocDialogService>(ShareDocDialogService);
  const isEntityTypeTarget = authenticationEntityType === 'target';

  useEffect(() => {
    if (isEntityTypeTarget) {
      if (data.targetName) {
        setDefaultTargetName(data.targetName);
        return;
      }

      setLoadingTargetNames(true);
      TargetAPI.getTargets()
        .then(res => {
          const prefix = `${TARGET_TYPES[targetType].displayName} Target`;
          setDefaultTargetName(
            getNextEntityNameForDefaultName(prefix, res.data, target => target.name)
          );
        })
        .finally(() => setLoadingTargetNames(false));
    }
  }, []);

  const sourceTypeMetaData = useMemo(() => {
    if (provider.entityType !== AccountEntityTypes.SOURCE) {
      return null;
    }
    return SOURCE_TYPES[provider.connectorType];
  }, [provider.connectorType, provider.entityType]);

  const onNewAuthAccount = (authAccountId, authAccounts) => {
    eventTrack({
      action: TRACKER_NEW_AUTH_ACCOUNT,
      properties: defaultAnalyticsData
    });

    setAuthorisedAccountId(authAccountId);

    onContinue(authAccountId, authAccounts);
  };

  const fetchAuthAccounts = () => {
    setLoadingAuthAccounts(true);
    setPageError(null);
    // Skipping delay.
    AuthAPI.getAuthTokens(
      provider.type,
      getAddAccountSuccessRedirectionPath(angularRouterService),
      removeParamAndFetchUrl(angularRouterService, AUTH_ERROR_MESSAGE_PARAM_NAME)
    )
      .then(
        res => {
          setConsentUrls(res.data.urls);

          if (!res.data.tokens) {
            return;
          }

          const authAccounts = authorizedAccountsFactory(
            res.data.tokens,
            ConnectorAuthTypeEnum.OAUTH
          ) as AuthorisedAccount[];

          setAuthorisedAccounts(authAccounts);

          if (authAccounts.length) {
            /**
             * Redirect to next step whenever user adds a new auth account,
             * Since this page has multiple entry points, we can use query params
             * as hint to know if the intention is to redirect.
             */

            const searchParams = new URLSearchParams(history.location.search);
            const authAccCreated = searchParams.get(AUTH_ACC_CREATED_QUERY_FLAG);

            if (authAccCreated && !isEntityTypeTarget) {
              let addedAuthorisedAccountId = authAccounts[0].id;
              const authAccCreatedAt = searchParams.get(AUTH_ACC_CREATED_AT_QUERY_FLAG);
              searchParams.delete(AUTH_ACC_CREATED_QUERY_FLAG);
              searchParams.delete(AUTH_ACC_CREATED_AT_QUERY_FLAG);

              if (authAccCreatedAt) {
                addedAuthorisedAccountId =
                  authAccounts.find(account => account.createdAt.toString() === authAccCreatedAt)
                    ?.id || addedAuthorisedAccountId;
              }

              history.replace({
                search: searchParams.toString()
              });

              /**
               * continue results in another navigation which can cancel the previous navigation
               */
              setTimeout(() => {
                onNewAuthAccount(addedAuthorisedAccountId, authAccounts);
              }, 0);
            } else {
              const isSelectedAccountIdValid =
                authorisedAccountId &&
                authAccounts.find(account => account.id === authorisedAccountId);

              if (isSelectedAccountIdValid) {
                return;
              }

              // on opening account in normal flow
              setAuthorisedAccountId(authAccounts[0].id);
            }
          }
        },
        err => {
          setLoadingAuthAccounts(false);
          setPageError(err);
        }
      )
      .finally(() => {
        setLoadingAuthAccounts(false);
      });
  };

  useEffect(() => {
    fetchAuthAccounts();
  }, []);

  const defaultAnalyticsData = useMemo(
    () => ({
      authType: ConnectorAuthTypeEnum.OAUTH,
      entityType: provider.entityType
    }),
    [provider.entityType]
  );

  const continueClick = () => {
    setTargetValidationFlow(false);

    eventTrack({
      action: TRACKER_AUTH_ACCOUNT_CONTINUE_CLICK,
      properties: defaultAnalyticsData
    });

    if (isEntityTypeTarget) {
      setTargetFormError(null);
      setSubmitting(true);
      eventTrack({
        action: TRACKER_NEW_TARGET_SUBMIT_CLICK,
        properties: {
          targetName: formikRef.current.values?.targetName,
          targetType
        }
      });
      // TargetFormError validate and call continue Next;

      setTargetValidationFlow(true);
      return;
    }

    onContinue();
  };

  const validateAdsAccount = (request, account) => {
    setSubmitting(true);
    request.then(
      () => {
        setSubmitting(false);
        setAdsAccountError(false);

        continueNext({
          account
        });
      },
      err => {
        if (sourceTypeMetaData.name === 'AMAZON_ADS' && getBackendErrorCodeFromObj(err) !== 4001) {
          return;
        }

        setSubmitting(false);
        setAdsAccountError(true);

        eventTrack({
          action: TRACKER_VALID_ADS_ACCOUNT_FAILED,
          properties: {
            error: err
          }
        });
      }
    );
  };

  const onContinue = (selectedAccountId = authorisedAccountId, accounts = authorisedAccounts) => {
    if (!data.account || data.account.id !== selectedAccountId) {
      markDirty();
    }

    const account = accounts.find(acc => acc.id === selectedAccountId);

    eventTrack({
      action: TRACKER_AUTH_ACCOUNT_CONTINUE,
      properties: defaultAnalyticsData
    });

    if (isEntityTypeTarget) {
      // Create Target
      const targetConfig = {
        oauth_token_id: selectedAccountId
      };

      TargetAPI.createTarget(targetType, targetName, targetConfig).then(
        res => {
          if (res.data) {
            const target = TargetFactory(res.data);

            eventTrack({
              action: TRACKER_NEW_TARGET_SUCCESS,
              properties: {
                target: res.data
              }
            });

            setSubmitting(false);

            continueNext({
              target,
              targetName
            });
          }
        },
        err => {
          setSubmitting(false);
          const errorMessage = getErrorMessageFromObj(err);
          setTargetFormError(errorMessage);
          setTargetValidationFlow(false);
          eventTrack({
            action: TRACKER_NEW_TARGET_FAILED,
            properties: {
              error: errorMessage
            }
          });
        }
      );

      return;
    }

    if (sourceTypeMetaData?.canValidateAdsAccount) {
      const request = validateAdsAccountRequest(sourceTypeMetaData.name, selectedAccountId);

      if (request !== null) {
        // Validate Payload
        validateAdsAccount(request, account);
        return;
      }
    }

    continueNext({
      account
    });
  };

  const inviteTeamMember = () => {
    shareDialogService.show({
      docLink: sourceTypeMetaData.docLink,
      displayName: provider.typeDisplay,
      entity: HevoEntity.PIPELINE.value,
      type: sourceTypeMetaData.name
    });

    eventTrack({
      action: TRACKER_SOURCE_CONFIG_INVITE_MEMBER_LINK_CLICK,
      properties: {
        sourceType: sourceTypeMetaData?.parent
          ? sourceTypeMetaData.parent
          : sourceTypeMetaData.name,
        derivedSourceType: sourceTypeMetaData?.name
      }
    });
  };

  const onNewAccountClick = (newAuth = false) => {
    if (newAuth) {
      eventTrack({
        action: TRACKER_ADD_MORE_AUTH_ACCOUNT_CLICK,
        properties: defaultAnalyticsData
      });
    } else {
      eventTrack({
        action: TRACKER_ADD_FIRST_AUTH_ACCOUNT_CLICK,
        properties: defaultAnalyticsData
      });
    }

    eventTrack({
      action: TRACKER_ADD_AUTH_ACCOUNT_CLICK,
      properties: defaultAnalyticsData
    });

    if (consentUrls && consentUrls.length === 1) {
      window.location.assign(consentUrls[0].url);
      return;
    }

    setShowEnvironmentSelection(true);
  };

  const heading = useMemo(() => {
    if (authenticationEntityType === 'target') {
      return `Add the ${provider.typeDisplay} Target`;
    }

    if (provider.linkedAccount) {
      return `Configure your ${provider.linkedAccount} account linked to ${provider.typeDisplay}`;
    }
    return `Configure your ${provider.typeDisplay} account`;
  }, [provider, authenticationEntityType]);

  const getAccountEnvironmentLabel = (env: AuthAccountEnvironment) =>
    getAuthEnvironmentLabel(env, provider.authAccountEnvironmentsMetaData);

  const entityName = useMemo(() => {
    if (authenticationEntityType === 'source') {
      return 'Source';
    }

    if (authenticationEntityType === 'target') {
      return 'Target';
    }

    return getDestinationModeKeywords(destinationMode).singular;
  }, [authenticationEntityType, destinationMode]);

  const entityDocumentation = useMemo(() => {
    const { type } = provider;

    if (authenticationEntityType === 'source') {
      return SOURCE_TYPES[provider?.connectorType]?.docLink
        ? SOURCE_TYPES[provider.connectorType].docLink
        : null;
    }

    if (authenticationEntityType === 'target') {
      return TARGET_TYPES[provider?.connectorType]?.docLink
        ? TARGET_TYPES[provider.connectorType].docLink
        : null;
    }

    if (
      destinationMode === DestinationMode.WAREHOUSE &&
      DESTINATION_TYPES[type]?.warehouseDocLink
    ) {
      return DESTINATION_TYPES[type].warehouseDocLink;
    }

    return DESTINATION_TYPES[type]?.docLink ? DESTINATION_TYPES[type].docLink : null;
  }, [authenticationEntityType, destinationMode, provider]);

  const listTitle = useMemo(() => {
    if (provider.linkedAccount) {
      return `Select your ${provider.linkedAccount} account linked to ${provider.typeDisplay}`;
    }
    return `Select your ${provider.typeDisplay} account`;
  }, [provider]);

  const authTriggerLabel = useMemo(() => {
    let displayName = provider.typeDisplay;

    if (provider.linkedAccount) {
      displayName = provider.linkedAccount;
    }

    return `Add ${displayName} Account`;
  }, [provider]);

  const shouldShowInfoMessagePane = useMemo(
    () => provider.connectorType in SOURCE_INFO_MESSAGE_PANE_MAP,
    [provider.connectorType]
  );

  const sourceInfoMessagePane = useMemo(
    () => (shouldShowInfoMessagePane ? SOURCE_INFO_MESSAGE_PANE_MAP[provider.connectorType] : null),
    [provider.connectorType, shouldShowInfoMessagePane]
  );

  const deleteAccount = accountId => {
    AuthAPI.deleteAccount(provider.type, accountId).then(() => {
      const filteredAccounts = authorisedAccounts.filter(
        authorisedAccount => authorisedAccount.id !== accountId
      );

      if (authorisedAccountId === accountId) {
        if (filteredAccounts.length > 0) {
          setAuthorisedAccountId(filteredAccounts[0].id);
        } else {
          setAuthorisedAccountId(null);
        }
      }

      setAuthorisedAccounts(filteredAccounts);
    });
  };

  const selectAuthorisedAccount = accountId => {
    setAuthorisedAccountId(accountId);
    eventTrack({
      action: TRACKER_AUTH_ACCOUNT_CLICK,
      properties: defaultAnalyticsData
    });
  };

  const targetNameValidator = (inputValue: string, options) => {
    updateData({
      targetName: inputValue
    });

    return TargetAPI.validateTargetName(inputValue, options).catch(e => {
      if (isPromiseCancelError(e)) {
        return;
      }

      // eslint-disable-next-line no-throw-literal
      throw { ...e, customMessage: `A Target with name, <b>"${inputValue}"</b> already exists.` };
    });
  };

  useEffect(() => {
    if (queueLength === 0 && targetValidationFlow) {
      const { errors } = formikRef.current;
      if (Object.keys(errors).length > 0) {
        // to trigger error focus
        formikRef.current.submitForm();

        setTargetValidationFlow(false);
        setSubmitting(false);
      } else {
        onContinue();
      }
    }
  }, [queueLength, targetValidationFlow]);

  const loading = loadingAuthAccounts || loadingTargetNames;

  return (
    <>
      {(consentUrls.length > 0 || loading) && !adsAccountError && (
        <div className='bg-surface scrollable-content' data-id={generateDataId('content')}>
          <div className='body-container'>
            <div className='header-container'>
              <div className='header' data-id={generateDataId('heading')}>
                {heading}
              </div>

              {!loading && entityDocumentation && (
                <div className='subheader'>
                  To know in detail about configuring this {entityName}, read the
                  <HdDocLink
                    dataId={generateDataId('entityDocumentation')}
                    className='ml-1'
                    section='select_auth_account'
                    docLink={entityDocumentation}
                    label='Documentation'
                  />
                </div>
              )}
            </div>

            <div
              className={clsx(
                styles.authContent,
                shouldShowInfoMessagePane && styles.authContentWithMessagePane
              )}
            >
              {/** List Auth */}
              {!loading && authorisedAccounts.length > 0 && (
                <>
                  <div
                    className='mb-7 text-bold text-center'
                    data-id={generateDataId('list-title')}
                  >
                    {listTitle}
                  </div>

                  <AuthAccountsBox
                    authorisedAccounts={authorisedAccounts}
                    selectedAccountId={authorisedAccountId}
                    provider={provider.type}
                    connectorType={provider.connectorType}
                    onAccountChange={(accountId: number) => selectAuthorisedAccount(accountId)}
                    entityType={provider.entityType}
                    isProviderTypeGoogle={provider.isAccountTypeGoogle}
                    onNewAccountClick={() => {
                      onNewAccountClick(true);
                    }}
                    accountEnvironmentLabelFn={env => getAccountEnvironmentLabel(env)}
                    authType={ConnectorAuthTypeEnum.OAUTH}
                    onDeleteAccount={(accountId: number) => deleteAccount(accountId)}
                  />

                  {isEntityTypeTarget && (
                    <Formik
                      initialValues={{
                        targetName: defaultTargetName
                      }}
                      validationSchema={validationSchema}
                      onSubmit={() => {}}
                      innerRef={formikRef}
                      dataId={generateDataId('targetNameForm')}
                    >
                      {props => (
                        <Box
                          component='form'
                          className='mt-7'
                          onSubmit={props.handleSubmit}
                          noValidate
                        >
                          <ErrorFocus formik={props} />

                          <HdFormControl>
                            <Field
                              name='targetName'
                              label='Target Name'
                              required
                              component={HdFormikTextFieldValidator}
                              asyncValidator={targetNameValidator}
                              helperText='A unique name for your Target'
                              autoFocus
                              maxLength={255}
                              asyncMonitor={processGoingOn => {
                                if (processGoingOn) {
                                  addProcess(TARGET_NAME);
                                } else {
                                  removeProcess(TARGET_NAME);
                                }
                              }}
                            />
                          </HdFormControl>
                        </Box>
                      )}
                    </Formik>
                  )}
                </>
              )}

              {/** New Auth */}
              {!loading && !authorisedAccounts.length && (
                <div className='center-flex-col'>
                  <NewAuthTrigger
                    flatButton={false}
                    onAuthClick={() => onNewAccountClick()}
                    providerClass={provider.providerClass}
                    providerIcon={provider.icon}
                    triggerLabel={authTriggerLabel}
                  />
                </div>
              )}

              {/** Info Message Pane */}
              {!loading && shouldShowInfoMessagePane && (
                <HdPane
                  dataId={generateDataId('info-message-pane')}
                  disableHide
                  className='mt-5 px-3 py-2'
                  icon='error-filled'
                  iconClasses='text-warning'
                  variant='warning-faded'
                >
                  <div className='text-default text-body-2'>
                    {sourceInfoMessagePane.message}
                    {sourceInfoMessagePane.docLink && (
                      <HdDocLink
                        dataId={generateDataId(`${provider?.connectorType}-provider`)}
                        section='auth-provider'
                        docLink={sourceInfoMessagePane.docLink}
                      />
                    )}
                  </div>
                </HdPane>
              )}

              {/** Redirection Error Message Pane */}
              {!loading && redirectedErrorMessage && (
                <HdPane
                  icon='error-filled'
                  variant='error-faded'
                  disableHide
                  className={clsx(styles.errorBorder, 'error mt-5 px-3 py-3')}
                  dataId={generateDataId('redirection-error')}
                >
                  <span>
                    {redirectedErrorMessage}
                    {AUTH_FAIL_DOC_LINK_MAP[provider.connectorType] && (
                      <HdDocLink
                        dataId={generateDataId(`redirection-error`)}
                        section='auth-page'
                        docLink={AUTH_FAIL_DOC_LINK_MAP[provider.connectorType]}
                      />
                    )}
                  </span>
                </HdPane>
              )}

              {loading && <LoadingShimmer />}

              {targetFormError && !loading && (
                <FormError dataId={generateDataId('create-target-error')}>
                  {targetFormError}
                </FormError>
              )}

              {authorisedAccounts.length > 0 && (
                <div className='center-flex-row justify-center mt-7'>
                  <HdTooltip title='Select an account to proceed' disabled={!!authorisedAccountId}>
                    <HdButton
                      dataId={generateDataId('continue')}
                      disabled={submitting || !authorisedAccountId}
                      onClick={() => continueClick()}
                      showProgress={submitting}
                    >
                      Continue
                    </HdButton>
                  </HdTooltip>
                </div>
              )}

              {!loading && <SourceTermsAndServices connectorType={provider.connectorType} />}
            </div>
          </div>
        </div>
      )}

      {/** Ads account error */}
      {!adsAccountError && !!error && !loading && (
        <RetryApiAlert
          dataId={generateDataId('')}
          error={error}
          actionHandler={() => fetchAuthAccounts()}
        />
      )}

      {adsAccountError && !loading && (
        <AdsAccountError
          docLink={ADS_ACCOUNT_SETUP_DOC_LINK_MAP[provider.connectorType]}
          accountEmail={authorisedAccountEmail}
          accountType={provider.typeDisplay}
          onInviteMemberClick={inviteTeamMember}
          onTryAgainClick={() => setAdsAccountError(false)}
        />
      )}

      <AuthEnvironmentSelection
        authAccountEnvironmentsMetaData={provider.authAccountEnvironmentsMetaData}
        consentUrls={consentUrls}
        isOpen={showEnvironmentSelection}
        onClose={() => {
          setShowEnvironmentSelection(false);
        }}
        providerDisplay={provider.linkedAccount || provider.typeDisplay}
      />
    </>
  );
}
